import { useState, useEffect, useRef, useCallback } from 'react';

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

interface UseSidebarCollapsedReturn {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
}

const getStoredValue = (): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  try {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (!stored) {
      return false;
    }

    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed === 'boolean') {
      return parsed;
    }

    localStorage.removeItem(SIDEBAR_COLLAPSED_KEY);
    return false;
  } catch {
    try {
      localStorage.removeItem(SIDEBAR_COLLAPSED_KEY);
    } catch {
      // silently fail
    }
    return false;
  }
};

const setStoredValue = (value: boolean): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(value));
  } catch {
    // silently fail if localStorage is full or unavailable
  }
};

const useSidebarCollapsed = (): UseSidebarCollapsedReturn => {
  const [isCollapsed, setIsCollapsed] = useState(() => getStoredValue());
  const isCollapsedRef = useRef(isCollapsed);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedValue = getStoredValue();
      isCollapsedRef.current = storedValue;
      setIsCollapsed(storedValue);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sidebarCollapsedChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarCollapsedChange', handleStorageChange);
    };
  }, []);

  const toggleCollapsed = useCallback(() => {
    const newValue = !isCollapsedRef.current;
    isCollapsedRef.current = newValue;
    setStoredValue(newValue);
    setIsCollapsed(newValue);
    window.dispatchEvent(new Event('sidebarCollapsedChange'));
  }, []);

  return { isCollapsed, toggleCollapsed };
};

export default useSidebarCollapsed;
